import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Loader2, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LEGAL_PATHS = ["/terms", "/privacy", "/gdpr", "/cookies"];

export const TermsGate = () => {
  const { user, setUser, logout } = useAuth();
  const { pathname } = useLocation();
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (!user || !user.terms_required || LEGAL_PATHS.includes(pathname)) return null;

  const accept = async () => {
    setBusy(true);
    setError("");
    try {
      setUser((await api.post("/auth/accept-terms")).data);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open>
      <DialogContent data-testid="terms-gate-dialog" className="[&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <FileText className="text-emerald-600" />
          <DialogTitle className="font-display text-xl">{user.terms_version ? "We've updated our terms" : "Please accept our terms"}</DialogTitle>
          <DialogDescription>
            To keep using PostalCloud, please review and accept the current Terms of Service and Privacy Policy (version {user.terms_current_version}).
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-start gap-3">
          <Checkbox id="gate-terms" data-testid="terms-gate-checkbox" checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} className="mt-0.5" />
          <Label htmlFor="gate-terms" className="text-sm font-normal leading-relaxed">
            I agree to the <Link to="/terms" target="_blank" data-testid="terms-gate-terms-link" className="underline hover:text-emerald-600">Terms of Service</Link> and{" "}
            <Link to="/privacy" target="_blank" data-testid="terms-gate-privacy-link" className="underline hover:text-emerald-600">Privacy Policy</Link>
          </Label>
        </div>
        {error && <p data-testid="terms-gate-error" className="text-sm text-red-600">{error}</p>}
        <div className="flex gap-3 justify-end">
          <Button variant="outline" data-testid="terms-gate-logout-btn" onClick={logout} className="rounded-full">Sign out</Button>
          <Button data-testid="terms-gate-accept-btn" onClick={accept} disabled={!agreed || busy} className="rounded-full">
            {busy && <Loader2 size={15} className="animate-spin mr-2" />} Accept and continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
